import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';

const AdminEmbeddingModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    provider: '',
    model_identifier: '',
    display_label: '',
    is_active: true,
    embedding_dimension: '',
    max_input_tokens: '',
    pricing_per_million_tokens_usd: '',
    notes: '',
    supports_batch: false,
    api_endpoint: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('embedding_models')
      .select('id, provider, model_identifier, display_label, is_active, embedding_dimension, max_input_tokens, pricing_per_million_tokens_usd, notes, supports_batch, api_endpoint')
      .order('provider', { ascending: true })
      .order('display_label', { ascending: true, nullsFirst: false });
    if (error) setError(error.message);
    setModels(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      provider: '',
      model_identifier: '',
      display_label: '',
      is_active: true,
      embedding_dimension: '',
      max_input_tokens: '',
      pricing_per_million_tokens_usd: '',
      notes: '',
      supports_batch: false,
      api_endpoint: ''
    });
    setIsOpen(true);
  };
  
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      provider: row.provider || '',
      model_identifier: row.model_identifier || '',
      display_label: row.display_label || '',
      is_active: !!row.is_active,
      embedding_dimension: row.embedding_dimension ?? '',
      max_input_tokens: row.max_input_tokens ?? '',
      pricing_per_million_tokens_usd: row.pricing_per_million_tokens_usd ?? '',
      notes: row.notes ?? '',
      supports_batch: !!row.supports_batch,
      api_endpoint: row.api_endpoint ?? ''
    });
    setIsOpen(true);
  };
  
  const closeDlg = () => setIsOpen(false);

  const save = async () => {
    setError('');
    const payload = {
      provider: form.provider,
      model_identifier: form.model_identifier,
      display_label: form.display_label,
      is_active: !!form.is_active,
      embedding_dimension: form.embedding_dimension === '' ? null : Number(form.embedding_dimension),
      max_input_tokens: form.max_input_tokens === '' ? null : Number(form.max_input_tokens),
      pricing_per_million_tokens_usd: form.pricing_per_million_tokens_usd === '' ? null : Number(form.pricing_per_million_tokens_usd),
      notes: form.notes || null,
      supports_batch: !!form.supports_batch,
      api_endpoint: form.api_endpoint || null
    };
    
    if (editing) {
      const { error } = await supabase.from('embedding_models').update(payload).eq('id', editing.id);
      if (error) return setError(error.message);
    } else {
      const { error } = await supabase.from('embedding_models').insert(payload);
      if (error) return setError(error.message);
    }
    setIsOpen(false);
    load();
  };

  const remove = async (row) => {
    setError('');
    const { error } = await supabase.from('embedding_models').delete().eq('id', row.id);
    if (error) return setError(error.message);
    load();
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>Manage Embedding Models</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" onClick={openNew}>Add Embedding Model</Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Identifier</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Dimension</TableCell>
                <TableCell align="right">Max Tokens</TableCell>
                <TableCell align="right">$ / 1M Tokens</TableCell>
                <TableCell>Batch Support</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.provider}</TableCell>
                  <TableCell>{m.model_identifier}</TableCell>
                  <TableCell>{m.display_label}</TableCell>
                  <TableCell>{m.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">{m.embedding_dimension ?? ''}</TableCell>
                  <TableCell align="right">{m.max_input_tokens ?? ''}</TableCell>
                  <TableCell align="right">{m.pricing_per_million_tokens_usd ?? ''}</TableCell>
                  <TableCell>{m.supports_batch ? 'Yes' : 'No'}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }} title={m.notes || ''}>{(m.notes || '').slice(0, 60)}{m.notes && m.notes.length > 60 ? '…' : ''}</TableCell>
                  <TableCell align="right">
                    <Button size="small" sx={{ mr: 1 }} onClick={() => openEdit(m)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => remove(m)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={isOpen} onClose={closeDlg} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Embedding Model' : 'Add Embedding Model'}</DialogTitle>
        <DialogContent>
          <TextField margin="normal" fullWidth label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
          <TextField margin="normal" fullWidth label="Model Identifier" helperText="Unique identifier used in code (e.g., bge-m3-embedding, text-embedding-3-large)" value={form.model_identifier} onChange={(e) => setForm({ ...form, model_identifier: e.target.value })} />
          <TextField margin="normal" fullWidth label="Display Label" value={form.display_label} onChange={(e) => setForm({ ...form, display_label: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField type="number" margin="normal" label="Embedding Dimension" helperText="e.g., 1024, 1536, 3072" value={form.embedding_dimension} onChange={(e) => setForm({ ...form, embedding_dimension: e.target.value })} fullWidth />
            <TextField type="number" margin="normal" label="Max Input Tokens" value={form.max_input_tokens} onChange={(e) => setForm({ ...form, max_input_tokens: e.target.value })} fullWidth />
          </Box>
          <TextField type="number" margin="normal" fullWidth label="Pricing per 1M Tokens (USD)" value={form.pricing_per_million_tokens_usd} onChange={(e) => setForm({ ...form, pricing_per_million_tokens_usd: e.target.value })} />
          <TextField margin="normal" fullWidth label="API Endpoint (optional)" helperText="Custom API endpoint if needed" value={form.api_endpoint} onChange={(e) => setForm({ ...form, api_endpoint: e.target.value })} />
          <TextField margin="normal" fullWidth label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={3} />
          <FormControlLabel control={<Switch checked={form.supports_batch} onChange={(e) => setForm({ ...form, supports_batch: e.target.checked })} />} label="Supports Batch Processing" />
          <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDlg}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEmbeddingModels;

